// Curve sampling helpers for the 2D sketch system.
// Each `sample*` returns Vec2[] — called by handlePolygon2D when walking a
// sketch value. Inputs are the tagged-record arrays produced by the
// `line`/`arc`/`bezier`/`spline`/`circle_entity` built-in functions.

export type Vec2 = [number, number];
export type Vec3 = [number, number, number];

// Tagged-record shapes (first element is the tag string)
export type LineEntity = ['line', Vec2, Vec2];
export type ArcEntity = ['arc', Vec2, number, number, number]; // center, r, startDeg, endDeg
export type CircleEntity = ['circle', Vec2, number]; // center, r
export type RectEntity = ['rect', Vec2, Vec2]; // two opposite corners (a, b)
export type BezierEntity = ['bezier', Vec2, Vec2, Vec2, Vec2]; // p0, c1, c2, p3
export type SplineEntity = ['spline', Vec2[]];
export type DimEntity = ['dim', ...unknown[]];
export type ImageEntity = ['image', ...unknown[]];
export type SketchEntity = LineEntity | ArcEntity | CircleEntity | RectEntity | BezierEntity | SplineEntity | DimEntity | ImageEntity;

export type SketchValue = ['sketch', string | Vec3, Vec3, SketchEntity[]];

export function isSketchValue(v: unknown): v is SketchValue {
  return Array.isArray(v) && v[0] === 'sketch';
}

export function entityTag(v: unknown): string | null {
  if (Array.isArray(v) && typeof v[0] === 'string') return v[0];
  return null;
}

// An entity is "construction" if its last element is a plain-object marker
// `{ construction: true }`. Such entities render dash-dot in the 2D editor
// and are skipped by walkEntities so they never enter the extruded profile.
export function isConstruction(v: unknown): boolean {
  if (!Array.isArray(v) || v.length < 2) return false;
  const last = v[v.length - 1];
  if (last && typeof last === 'object' && !Array.isArray(last)) {
    return (last as { construction?: unknown }).construction === true;
  }
  return false;
}

const TAU = Math.PI * 2;
const DEG = Math.PI / 180;

function toVec2(v: unknown): Vec2 {
  if (Array.isArray(v)) return [Number(v[0]) || 0, Number(v[1]) || 0];
  return [0, 0];
}

function segCountForArc(radius: number, angleDeg: number, fn: number): number {
  const frac = Math.abs(angleDeg) / 360;
  const n = Math.max(2, Math.ceil(Math.max(fn, 16) * frac));
  return n;
}

export function sampleLine(e: LineEntity): Vec2[] {
  return [toVec2(e[1]), toVec2(e[2])];
}

export function sampleArc(e: ArcEntity, fn: number): Vec2[] {
  const [, centerRaw, r, startDeg, endDeg] = e;
  const c = toVec2(centerRaw);
  // Always sweep CCW (OpenSCAD convention). If end < start, the delta would
  // be negative → CW sweep. Normalize by adding 360° so the arc goes the
  // other way around (through 180° rather than through 0°). This keeps the
  // geometry consistent with the 2D canvas renderer, which always draws CCW.
  let delta = endDeg - startDeg;
  if (delta < 0) delta += 360;
  const segs = segCountForArc(r, delta, fn);
  const pts: Vec2[] = [];
  for (let i = 0; i <= segs; i++) {
    const t = i / segs;
    const a = (startDeg + delta * t) * DEG;
    pts.push([c[0] + r * Math.cos(a), c[1] + r * Math.sin(a)]);
  }
  return pts;
}

export function sampleRect(e: RectEntity): Vec2[] {
  const a = toVec2(e[1]);
  const b = toVec2(e[2]);
  return [
    [a[0], a[1]],
    [b[0], a[1]],
    [b[0], b[1]],
    [a[0], b[1]],
    [a[0], a[1]],
  ];
}

export function sampleCircle(e: CircleEntity, fn: number): Vec2[] {
  const [, centerRaw, r] = e;
  const c = toVec2(centerRaw);
  const segs = Math.max(fn, 16);
  const pts: Vec2[] = [];
  for (let i = 0; i < segs; i++) {
    const a = (i / segs) * TAU;
    pts.push([c[0] + r * Math.cos(a), c[1] + r * Math.sin(a)]);
  }
  return pts;
}

export function sampleBezier(e: BezierEntity, fn: number): Vec2[] {
  const p0 = toVec2(e[1]);
  const c1 = toVec2(e[2]);
  const c2 = toVec2(e[3]);
  const p3 = toVec2(e[4]);
  const segs = Math.max(fn, 16);
  const pts: Vec2[] = [];
  for (let i = 0; i <= segs; i++) {
    const t = i / segs;
    const u = 1 - t;
    const b0 = u * u * u;
    const b1 = 3 * u * u * t;
    const b2 = 3 * u * t * t;
    const b3 = t * t * t;
    pts.push([
      b0 * p0[0] + b1 * c1[0] + b2 * c2[0] + b3 * p3[0],
      b0 * p0[1] + b1 * c1[1] + b2 * c2[1] + b3 * p3[1],
    ]);
  }
  return pts;
}

// Catmull-Rom through knots (tension alpha=0.5 = centripetal, avoids loops)
export function sampleSpline(e: SplineEntity, fn: number): Vec2[] {
  const raw = (Array.isArray(e[1]) ? e[1] : []).map(toVec2);
  // Drop consecutive duplicates — they create degenerate segments (p1==p2 or
  // p2==p3) that cause the Catmull-Rom formula to overshoot past the endpoint.
  const EPS = 1e-9;
  const knots: Vec2[] = [];
  for (const k of raw) {
    const prev = knots[knots.length - 1];
    if (!prev || Math.abs(prev[0] - k[0]) > EPS || Math.abs(prev[1] - k[1]) > EPS) knots.push(k);
  }
  if (knots.length < 2) return knots;
  const segsPerSegment = Math.max(Math.floor(fn / 4), 6);
  const pts: Vec2[] = [];
  // Extend endpoints by mirroring so start/end knots are included
  const ext: Vec2[] = [
    [2 * knots[0][0] - knots[1][0], 2 * knots[0][1] - knots[1][1]],
    ...knots,
    [2 * knots[knots.length - 1][0] - knots[knots.length - 2][0],
     2 * knots[knots.length - 1][1] - knots[knots.length - 2][1]],
  ];
  for (let i = 0; i < ext.length - 3; i++) {
    const [p0, p1, p2, p3] = [ext[i], ext[i + 1], ext[i + 2], ext[i + 3]];
    for (let s = 0; s < segsPerSegment; s++) {
      const t = s / segsPerSegment;
      const t2 = t * t;
      const t3 = t2 * t;
      const x = 0.5 * ((2 * p1[0]) +
        (-p0[0] + p2[0]) * t +
        (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * t2 +
        (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * t3);
      const y = 0.5 * ((2 * p1[1]) +
        (-p0[1] + p2[1]) * t +
        (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2 +
        (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3);
      pts.push([x, y]);
    }
  }
  // Close with final knot
  pts.push(knots[knots.length - 1]);
  return pts;
}

// Walk a sketch's entity array and produce a list of polygon paths for
// `CrossSection.ofPolygons(..., 'EvenOdd')`. Rules:
//
//   * `rect` and `circle` are always standalone closed paths (their own entry).
//   * Open-curve entities (line / arc / bezier / spline) are chained into
//     continuous paths by endpoint-matching — **source order doesn't matter**.
//     Any entity whose endpoint meets either end of the current chain is
//     appended (reversing its samples if needed). When no more can be
//     attached, the chain is closed out and a new chain starts from the
//     next unused entity.
//   * `dim` and unknown tags are skipped.
//
// Returning multiple paths lets EvenOdd fill rule handle both unions and
// holes consistently without the caller stitching segments together.
export function walkEntities(entities: unknown[], fn: number): Vec2[][] {
  // Endpoint-match tolerance for chain-walking. Sketch coords are rounded
  // to 0.01 mm (see round2 in SketchEditor2D), but a line snapped to an
  // arc's derived endpoint receives the rounded screen coord — while the
  // arc's sampled endpoint comes from the unrounded center + r * cos/sin.
  // Typical drift: a few thousandths of a mm. 0.02 mm (2× the quantum)
  // safely closes that gap without accidentally fusing distinct vertices.
  const EPS = 0.02;
  const eq = (a: Vec2, b: Vec2) => Math.hypot(a[0] - b[0], a[1] - b[1]) < EPS;

  const openSegments: Vec2[][] = [];
  const closedPaths: Vec2[][] = [];

  for (const e of entities) {
    const tag = entityTag(e);
    if (!tag || tag === 'dim' || tag === 'image') continue;
    if (isConstruction(e)) continue;
    switch (tag) {
      case 'line':   openSegments.push(sampleLine(e as LineEntity)); break;
      case 'arc':    openSegments.push(sampleArc(e as ArcEntity, fn)); break;
      case 'bezier': openSegments.push(sampleBezier(e as BezierEntity, fn)); break;
      case 'spline': openSegments.push(sampleSpline(e as SplineEntity, fn)); break;
      case 'circle': closedPaths.push(sampleCircle(e as CircleEntity, fn)); break;
      case 'rect': {
        const pts = sampleRect(e as RectEntity);
        if (pts.length >= 2 && eq(pts[0], pts[pts.length - 1])) pts.pop();
        if (pts.length >= 3) closedPaths.push(pts);
        break;
      }
      default: break;
    }
  }

  // Greedy endpoint matching: start each chain from the first unused segment
  // (preserving source order for the starting segment), then greedily attach
  // any remaining segment whose endpoint touches either end of the chain,
  // reversing segment samples if needed.
  const used = new Uint8Array(openSegments.length);
  const paths: Vec2[][] = [];
  while (true) {
    let startIdx = -1;
    for (let i = 0; i < used.length; i++) { if (!used[i]) { startIdx = i; break; } }
    if (startIdx === -1) break;
    used[startIdx] = 1;
    const chain: Vec2[] = openSegments[startIdx].slice();
    let extended = true;
    while (extended) {
      extended = false;
      for (let i = 0; i < openSegments.length; i++) {
        if (used[i]) continue;
        const s = openSegments[i];
        if (s.length < 2) { used[i] = 1; continue; }
        const sFirst = s[0];
        const sLast = s[s.length - 1];
        const cFirst = chain[0];
        const cLast = chain[chain.length - 1];
        if (eq(cLast, sFirst))      { for (let k = 1; k < s.length; k++) chain.push(s[k]); used[i] = 1; extended = true; break; }
        if (eq(cLast, sLast))       { for (let k = s.length - 2; k >= 0; k--) chain.push(s[k]); used[i] = 1; extended = true; break; }
        if (eq(cFirst, sLast))      { for (let k = s.length - 2; k >= 0; k--) chain.unshift(s[k]); used[i] = 1; extended = true; break; }
        if (eq(cFirst, sFirst))     { for (let k = 1; k < s.length; k++) chain.unshift(s[k]); used[i] = 1; extended = true; break; }
      }
    }
    if (chain.length >= 2 && eq(chain[0], chain[chain.length - 1])) chain.pop();
    if (chain.length >= 3) paths.push(chain);
  }

  return [...paths, ...closedPaths];
}

// Plane normal (extrusion direction) for a named plane or raw vector.
// Named plane convention: the missing letter's axis is the normal, positive.
//   XY → +Z, XZ → +Y, YZ → +X
export function planeNormal(plane: string | Vec3): Vec3 {
  if (typeof plane === 'string') {
    switch (plane.toUpperCase()) {
      case 'XY': return [0, 0, 1];
      case 'XZ': return [0, 1, 0];
      case 'YZ': return [1, 0, 0];
      default:   return [0, 0, 1];
    }
  }
  const [x, y, z] = plane;
  const m = Math.hypot(x, y, z) || 1;
  return [x / m, y / m, z / m];
}

// Row-major 3x3 rotation that carries local sketch axes onto world axes for
// the given plane. Convention: sketch Z = plane normal, sketch Y = world-up
// (SCAD +Z) projected onto the plane, sketch X = Y × Z. This keeps "up"
// consistent across all planes, including face-pick normals.
//
// XY: identity (sketch X→world X, Y→Y, Z→Z)
// XZ: X→X, Y→Z, Z→-Y  (extrusion points into +Y, sketch Y is world Z)
// YZ: X→Y, Y→Z, Z→X   (extrusion points into +X)
//
// Vector planes go through `upAlignedRotation`, which produces the same
// up-is-world-Z convention for any normal, falling back to SCAD +Y when the
// plane is horizontal (n parallel to ±Z).
//
// `arbitraryFallback` is retained for API stability but is no longer used —
// the up-aligned rotation supersedes the previous rotationFromTo path.
export function rotationForPlane(plane: string | Vec3, _arbitraryFallback: (from: Vec3, to: Vec3) => number[]): number[] {
  if (typeof plane === 'string') {
    switch (plane.toUpperCase()) {
      case 'XY': return [1, 0, 0,  0, 1, 0,  0, 0, 1];
      case 'XZ': return [1, 0, 0,  0, 0, -1, 0, 1, 0];
      case 'YZ': return [0, 0, 1,  1, 0, 0,  0, 1, 0];
    }
  }
  return upAlignedRotation(planeNormal(plane));
}

// Up-aligned rotation: build an orthonormal basis (X', Y', Z') where Z' is
// the plane normal and Y' is world-up projected onto the plane. Returns a
// row-major 3×3 matrix whose columns are X', Y', Z' (so R · [x, y, 0]^T
// gives the world-space position of a sketch point).
function upAlignedRotation(n: Vec3): number[] {
  const nx = n[0], ny = n[1], nz = n[2];
  // Project SCAD +Z onto the plane: Y' = +Z − (n·+Z)·n. If the plane is
  // horizontal (n ≈ ±Z), the projection collapses — use +Y as the up
  // reference instead, matching the named-plane fallback for XY.
  let yx: number, yy: number, yz: number;
  const dot = nz; // [0,0,1] · n
  yx = -dot * nx;
  yy = -dot * ny;
  yz = 1 - dot * nz;
  let mag = Math.hypot(yx, yy, yz);
  if (mag < 1e-6) {
    yx = 0; yy = 1; yz = 0;
    mag = 1;
  }
  yx /= mag; yy /= mag; yz /= mag;
  // X' = Y' × Z' (right-handed)
  const xx = yy * nz - yz * ny;
  const xy = yz * nx - yx * nz;
  const xz = yx * ny - yy * nx;
  return [
    xx, yx, nx,
    xy, yy, ny,
    xz, yz, nz,
  ];
}
